import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createTireMeasure,
  deleteTireMeasure,
  getTireMeasureById,
  getTireMeasuresList,
  updateTireMeasure,
  updateTireMeasureStatus,
  type TireMeasureListParams,
  type TireMeasurePayload,
  type TireMeasureStatusPayload,
} from "../../services/administrator/tireMeasureService";

export const useQueryGetTireMeasuresList = (
  params: TireMeasureListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tireMeasureDetail, params, language],
      queryFn: () => getTireMeasuresList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetTireMeasureById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tireMeasureDetail, id, language],
    queryFn: () => getTireMeasureById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateTireMeasure = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TireMeasurePayload) => createTireMeasure(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMeasureDetail,
      }),
  });
};

export const useMutationUpdateTireMeasure = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: TireMeasurePayload & { id: string | number };
    }) => updateTireMeasure(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireMeasureDetail,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireMeasureDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateTireMeasureStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, active }: TireMeasureStatusPayload) =>
      updateTireMeasureStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMeasureDetail,
      }),
  });
};

export const useMutationDeleteTireMeasure = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteTireMeasure(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMeasureDetail,
      }),
  });
};
